import I18n from 'I18n';
import { memo, useMemo } from 'react';
import UILink from 'UIComponents/link/UILink';
import styled from 'styled-components';
import PortalIdParser from 'PortalIdParser';
import FormattedMessage from 'I18n/components/FormattedMessage';
import HR from 'UIComponents/elements/HR';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIFlex from 'UIComponents/layout/UIFlex';
import { FLINT, HEFFALUMP } from 'HubStyleTokens/colors';
import { getPropertyValue } from 'marketing-platform-lib/utils/crmUtils';
import CampaignsPlatformLabelPopoverOwner from './CampaignsPlatformLabelPopoverOwner';
import CampaignsPlatformLabelPopoverBusinessUnit from './CampaignsPlatformLabelPopoverBusinessUnit';
import CampaignsPlatformLabelPopoverProperty from './CampaignsPlatformLabelPopoverProperty';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SectionWrapper = styled.section.withConfig({
  displayName: "CampaignsPlatformLabelPopover__SectionWrapper",
  componentId: "sc-1rzhr3q-0"
})(["display:flex;flex-direction:column;gap:20px;margin:0;"]);
const CampaignDotWrapper = styled.div.withConfig({
  displayName: "CampaignsPlatformLabelPopover__CampaignDotWrapper",
  componentId: "sc-1rzhr3q-1"
})(["display:inline-block;margin-left:5px;line-height:20px;height:12px;width:12px;position:relative;"]);
export const CampaignDot = styled.span.withConfig({
  displayName: "CampaignsPlatformLabelPopover__CampaignDot",
  componentId: "sc-1rzhr3q-2"
})(["border-radius:50%;display:inline-block;height:12px;margin:0;width:12px;position:absolute;top:2px;background:", ";"], props => props.color);
const CampaignColorSidebarLeft = styled.div.withConfig({
  displayName: "CampaignsPlatformLabelPopover__CampaignColorSidebarLeft",
  componentId: "sc-1rzhr3q-3"
})(["position:absolute;background-color:", ";left:-1px;top:-1px;bottom:-1px;width:6px;border-radius:2px 0 0 2px;"], props => props.color);
function CampaignName({
  crmObject
}) {
  const {
    color,
    name
  } = useMemo(() => {
    return {
      color: getPropertyValue(crmObject, 'hs_color_hex', ''),
      name: getPropertyValue(crmObject, 'hs_name')
    };
  }, [crmObject]);
  return /*#__PURE__*/_jsxs(UILink, {
    href: `/marketing/${PortalIdParser.get()}/campaigns/${getPropertyValue(crmObject, 'hs_origin_asset_id')}`,
    children: [name, color && /*#__PURE__*/_jsx(CampaignDotWrapper, {
      children: /*#__PURE__*/_jsx(CampaignDot, {
        color: color
      })
    })]
  });
}
function CampaignsPlatformLabelPopover({
  campaignCrmObject
}) {
  const businessUnit = getPropertyValue(campaignCrmObject, 'hs_all_assigned_business_unit_ids');
  const owner = getPropertyValue(campaignCrmObject, 'hs_owner');
  const startDate = getPropertyValue(campaignCrmObject, 'hs_start_date');
  const endDate = getPropertyValue(campaignCrmObject, 'hs_end_date');
  return /*#__PURE__*/_jsxs(SectionWrapper, {
    "data-test-id": "campaign-popover",
    children: [/*#__PURE__*/_jsx(SectionWrapper, {
      style: {
        padding: '20px 20px 0 20px'
      },
      children: /*#__PURE__*/_jsx(CampaignsPlatformLabelPopoverProperty, {
        label: /*#__PURE__*/_jsxs("b", {
          style: {
            color: HEFFALUMP
          },
          children: [/*#__PURE__*/_jsx(UIIcon, {
            color: FLINT,
            name: "campaigns",
            className: "m-right-2"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "campaignSelect.formControlLabel"
          })]
        }),
        value: /*#__PURE__*/_jsx(CampaignName, {
          crmObject: campaignCrmObject
        })
      })
    }), /*#__PURE__*/_jsx(HR, {
      distance: "flush"
    }), /*#__PURE__*/_jsxs(SectionWrapper, {
      as: "dl",
      style: {
        padding: '0 20px 20px 20px'
      },
      children: [businessUnit && /*#__PURE__*/_jsx(CampaignsPlatformLabelPopoverBusinessUnit, {
        businessUnitId: parseInt(businessUnit, 10)
      }), /*#__PURE__*/_jsx(CampaignsPlatformLabelPopoverProperty, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "campaignSelect.popover.owner"
        }),
        value: owner ? /*#__PURE__*/_jsx(CampaignsPlatformLabelPopoverOwner, {
          ownerId: parseInt(owner, 10)
        }) : '--'
      }), /*#__PURE__*/_jsx(CampaignsPlatformLabelPopoverProperty, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "campaignSelect.popover.goal"
        }),
        value: getPropertyValue(campaignCrmObject, 'hs_goal', '--')
      }), /*#__PURE__*/_jsxs(UIFlex, {
        direction: "row",
        justify: "between",
        children: [/*#__PURE__*/_jsx(CampaignsPlatformLabelPopoverProperty, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "campaignSelect.popover.startDate"
          }),
          value: startDate ? I18n.moment(parseInt(startDate, 10)).format('L') : '--'
        }), /*#__PURE__*/_jsx(CampaignsPlatformLabelPopoverProperty, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "campaignSelect.popover.endDate"
          }),
          value: endDate ? I18n.moment(parseInt(endDate, 10)).format('L') : '--'
        })]
      }), /*#__PURE__*/_jsx(CampaignsPlatformLabelPopoverProperty, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "campaignSelect.popover.notes"
        }),
        value: getPropertyValue(campaignCrmObject, 'hs_notes', '--')
      })]
    }), /*#__PURE__*/_jsx(CampaignColorSidebarLeft, {
      color: getPropertyValue(campaignCrmObject, 'hs_color_hex', '')
    })]
  });
}
export default /*#__PURE__*/memo(CampaignsPlatformLabelPopover);